import React from 'react';
import { addWeeks, addMonths, format, isValid, parseISO } from 'date-fns';
import { Calendar, Clock, Repeat } from 'lucide-react';

interface RecurringBookingFormProps {
  isRecurring: boolean;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  startDate: string;
  endDate: string;
  onIsRecurringChange: (value: boolean) => void;
  onFrequencyChange: (value: 'weekly' | 'biweekly' | 'monthly') => void;
  onEndDateChange: (value: string) => void;
}

export default function RecurringBookingForm({
  isRecurring,
  frequency,
  startDate,
  endDate,
  onIsRecurringChange,
  onFrequencyChange,
  onEndDateChange,
}: RecurringBookingFormProps) {
  // Calculate suggested end date based on frequency
  const getSuggestedEndDate = () => {
    if (!startDate) {
      console.warn('Start date is required to calculate suggested end date');
      return null;
    }

    const parsedDate = parseISO(startDate);
    if (!isValid(parsedDate)) {
      console.warn('Invalid start date provided');
      return null;
    }

    try {
      switch (frequency) {
        case 'weekly':
          return format(addWeeks(parsedDate, 12), 'yyyy-MM-dd'); // 3 months
        case 'biweekly':
          return format(addWeeks(parsedDate, 24), 'yyyy-MM-dd'); // 6 months
        case 'monthly':
          return format(addMonths(parsedDate, 12), 'yyyy-MM-dd'); // 1 year
        default:
          return format(addMonths(parsedDate, 3), 'yyyy-MM-dd');
      }
    } catch (error) {
      console.error('Error calculating suggested end date:', error);
      return null;
    }
  };

  const formatDisplayDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMMM d, yyyy') : 'Invalid date';
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <div className="pt-6">
      <label className="flex items-center gap-2 mb-4">
        <input
          type="checkbox"
          checked={isRecurring}
          onChange={(e) => onIsRecurringChange(e.target.checked)}
          className="w-4 h-4 text-gray-800 border-gray-300 rounded focus:ring-gray-800"
        />
        <span className="font-medium flex items-center gap-2">
          <Repeat className="w-5 h-5 text-gray-800 dark:text-gray-300" />
          Make this a recurring booking
        </span>
      </label>

      {isRecurring && (
        <div className="grid md:grid-cols-2 gap-4 pl-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Frequency
            </label>
            <select
              value={frequency}
              onChange={(e) => onFrequencyChange(e.target.value as 'weekly' | 'biweekly' | 'monthly')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
                min={startDate}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const suggestedDate = getSuggestedEndDate();
                if (suggestedDate) {
                  onEndDateChange(suggestedDate);
                }
              }}
              className="mt-1 text-sm text-gray-800 dark:text-gray-300 hover:text-black dark:hover:text-white"
              disabled={!startDate}
            >
              Suggest end date
            </button>
          </div>

          {startDate && endDate && (
            <div className="md:col-span-2 bg-gray-50 dark:bg-gray-700/50 p-4 border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">Recurring Booking Details</h4>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Service will repeat {frequency}
                </li>
                <li className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Starting from {formatDisplayDate(startDate)}
                </li>
                <li className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Until {formatDisplayDate(endDate)}
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}